import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

// Who shows the notice (the one SaveErrorNotice on screen). A plain listener, so a save anywhere (a hook, a
// screen, outside any provider) can tell it
let showNotice: (() => void) | undefined;

/** A change couldn't be saved (e.g. a draft save's `.catch`): tells the person, instead of losing it quietly. */
export const notifySaveError = (): void => showNotice?.();

const Notice = styled.div`
  position: fixed;
  left: 50%;
  bottom: 1.5rem;
  z-index: 1000;
  display: flex;
  gap: 1rem;
  align-items: center;
  width: max-content;
  max-width: calc(100vw - 2rem);
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  background: var(--color-feedback-danger);
  color: var(--color-neutral-white);
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
  transform: translateX(-50%);

  button {
    flex-shrink: 0;
    font-weight: 600;
    text-decoration: underline;
  }
`;

/** The notice of a change that couldn't be saved; once in the app, over every screen. */
export const SaveErrorNotice = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    showNotice = () => setVisible(true);
    return () => {
      showNotice = undefined;
    };
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <Notice role="alert">
      <span>{t("components.saveErrorNotice.message")}</span>
      <button type="button" onClick={() => setVisible(false)}>
        {t("components.saveErrorNotice.close")}
      </button>
    </Notice>
  );
};
